// Or from '@reduxjs/toolkit/query' if not using the auto-generated hooks
import {BaseQueryFn, createApi, FetchArgs, fetchBaseQuery, FetchBaseQueryError,} from '@reduxjs/toolkit/query/react';
import {showError} from './errorSlice';
import config from '../config';

const NO_REDIRECT_ENDPOINTS = [
  { method: 'GET', path: '/auth/inspect' },
  { method: 'GET', path: '/profiles/' },
  { method: 'GET', path: '/trips/byUser/' },
  { method: 'GET', path: '/interactions/byTrip/' }
];


const isNoRedirectEndpoint = (method: string, url: string): boolean => {
  return NO_REDIRECT_ENDPOINTS.some(
    (endpoint) => method.toUpperCase() === endpoint.method && url.includes(endpoint.path),
  );
};

// Custom baseQuery to handle 401/403 globally
const rawBaseQuery = fetchBaseQuery({
  baseUrl: config.api.baseUrl,
  credentials: config.api.credentials,
});

// Shared promise to handle concurrent refresh attempts
let refreshPromise: Promise<any> | null = null;

const baseQueryWithAuth: BaseQueryFn<string | FetchArgs, unknown, FetchBaseQueryError> = async (
  args,
  api,
  extraOptions,
) => {
  const result = await rawBaseQuery(args, api, extraOptions);

  if (result.error) {
    // Handle 401/403 with token refresh
    const method = typeof args === 'object' && 'method' in args ? args.method || 'GET' : 'GET';
    const url = typeof args === 'object' && 'url' in args ? args.url : args;
    if (result.error.status === 401 || result.error.status === 403) {
      // Extract method and URL from args

      // Don't attempt refresh if this is a refresh request to prevent infinite loops
      const isRefreshRequest = typeof args === 'object' && args.url === '/auth/refresh';

      // Don't attempt refresh/redirect for auth-optional endpoints
      // These endpoints can work without authentication or use alternative auth (link_secret)
      if (isNoRedirectEndpoint(method, url)) {
        // Just return the error - no refresh, no redirect
        return result;
      }

      if (!isRefreshRequest) {
        // If a refresh is already in progress, wait for it
        if (refreshPromise) {
          await refreshPromise;
          // After the refresh completes, retry the original request
          return await rawBaseQuery(args, api, extraOptions);
        }

        // Start a new refresh
        refreshPromise = (async () => {
          try {
            const refreshResult = await rawBaseQuery(
              { url: '/auth/refresh', method: 'POST' },
              api,
              extraOptions,
            );
            return refreshResult;
          } finally {
            // Clear the promise when done (success or failure)
            refreshPromise = null;
          }
        })();

        const refreshResult = await refreshPromise;

        // If refresh was successful, retry the original request
        if (!refreshResult.error) {
          return await rawBaseQuery(args, api, extraOptions);
        } else {
          window.location.assign('/signout');
        }
      }
    } else if(method.toLowerCase() !== "get") {
        const errorMessage = 'Something went wrong. Please try again.';
        api.dispatch(showError(errorMessage));
      } else {
      console.error("api request failure"  + method + " " + url)
    }
  }

  return result;
};

// initialize an empty api service that we'll inject endpoints into later as needed
export const emptyApi = createApi({
  baseQuery: baseQueryWithAuth,
  endpoints: () => ({}),
});
